import React from 'react';
import { CheckCircle, Lock, Circle } from 'lucide-react';

const STAGE_LABELS = {
  'Service Agreement Signing': { short: 'Agreement', icon: '📝' },
  'Document Checklist Upload': { short: 'Documents', icon: '📄' },
  'Documents Verification': { short: 'Verification', icon: '🔍' },
  'Final Application Review': { short: 'Review', icon: '📋' },
  'Visa Fee Payment': { short: 'Payment', icon: '💳' },
  'Visa Application Filing': { short: 'Filing', icon: '📤' },
  'Application Under Review': { short: 'Processing', icon: '⏳' },
  'Visa Decision': { short: 'Decision', icon: '✅' },
  'Case Completed': { short: 'Complete', icon: '🎉' },
};

export default function ProcessStages({ stages, currentStage }) {
  if (!stages || stages.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <p className="text-slate-500 text-center">Loading process stages...</p>
      </div>
    );
  }

  const currentIndex = stages.findIndex(s => s.is_current);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="process-stages-card">
      <h3 className="text-xl font-bold text-slate-900 mb-6">Visa Application Process</h3>
      
      {/* Desktop View - Horizontal Timeline */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Progress Line Background */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-slate-200 rounded-full"></div>
          
          {/* Progress Line Completed */}
          <div 
            className="absolute top-5 left-0 h-1 bg-teal-500 rounded-full transition-all duration-500"
            style={{ width: `${(currentIndex / (stages.length - 1)) * 100}%` }}
          ></div>
          
          {/* Stage Nodes */}
          <div className="relative flex justify-between">
            {stages.map((stage, index) => {
              const stageInfo = STAGE_LABELS[stage.stage] || { short: stage.stage, icon: '📌' };
              
              return (
                <div 
                  key={stage.stage} 
                  className="flex flex-col items-center"
                  data-testid={`stage-${index}`}
                >
                  {/* Node */}
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      stage.is_completed 
                        ? 'bg-teal-500 border-teal-500 text-white' 
                        : stage.is_current 
                          ? 'bg-white border-teal-500 text-teal-500 ring-4 ring-teal-100' 
                          : 'bg-slate-100 border-slate-300 text-slate-400'
                    }`}
                  >
                    {stage.is_completed ? (
                      <CheckCircle size={20} />
                    ) : stage.is_locked ? (
                      <Lock size={16} />
                    ) : (
                      <Circle size={16} />
                    )}
                  </div>
                  
                  {/* Label */}
                  <div className="mt-3 text-center max-w-[80px]">
                    <p className={`text-xs font-medium ${
                      stage.is_completed || stage.is_current 
                        ? 'text-slate-900' 
                        : 'text-slate-400'
                    }`}>
                      {stageInfo.short}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile View - Vertical Timeline */}
      <div className="md:hidden space-y-4">
        {stages.map((stage, index) => {
          const stageInfo = STAGE_LABELS[stage.stage] || { short: stage.stage, icon: '📌' };
          
          return (
            <div 
              key={stage.stage} 
              className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                stage.is_current 
                  ? 'bg-teal-50 border border-teal-200' 
                  : stage.is_completed 
                    ? 'bg-slate-50' 
                    : ''
              }`}
              data-testid={`stage-mobile-${index}`}
            >
              {/* Icon */}
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  stage.is_completed 
                    ? 'bg-teal-500 text-white' 
                    : stage.is_current 
                      ? 'bg-teal-100 text-teal-600 border-2 border-teal-500' 
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {stage.is_completed ? (
                  <CheckCircle size={20} />
                ) : stage.is_locked ? (
                  <Lock size={16} />
                ) : (
                  <span className="text-lg">{stageInfo.icon}</span>
                )}
              </div>
              
              {/* Text */}
              <div className="flex-1">
                <p className={`font-medium ${
                  stage.is_completed || stage.is_current 
                    ? 'text-slate-900' 
                    : 'text-slate-400'
                }`}>
                  {stage.stage}
                </p>
                <p className={`text-xs ${
                  stage.is_current 
                    ? 'text-teal-600' 
                    : stage.is_completed 
                      ? 'text-slate-500' 
                      : 'text-slate-400'
                }`}>
                  {stage.is_current ? 'Current Stage' : stage.is_completed ? 'Completed' : 'Upcoming'}
                </p>
              </div>
              
              {/* Status Badge */}
              {stage.is_current && (
                <span className="px-2 py-1 text-xs font-medium bg-teal-500 text-white rounded-full">
                  Active
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Current Stage Info */}
      <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg border border-teal-100">
        <p className="text-sm text-slate-600">Current Stage:</p>
        <p className="text-lg font-bold text-teal-700" data-testid="current-stage-name">
          {currentStage || 'Loading...'}
        </p>
      </div>
    </div>
  );
}
